import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { NoaIcon } from "@/components/assistant/NoaIcon";
import { useAuth } from "@/lib/auth";
import { AssistantPanel } from "./AssistantPanel";

// =============================================================================
// AssistantProvider — état global de l'agent IA « Noa ».
// =============================================================================
// Monté dans AppLayout : bouton flottant en bas à droite + panneau latéral.
// Raccourci clavier : Ctrl/Cmd + J. Le contexte de page (route) est transmis à
// l'edge function pour adapter les réponses et les suggestions.
// =============================================================================

interface AssistantContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Ouvre le panneau et pré-remplit le champ de saisie. */
  openWithPrompt: (prompt: string) => void;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error("useAssistant must be used within AssistantProvider");
  return ctx;
}

export function AssistantProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { user, roles } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const openWithPrompt = useCallback((prompt: string) => {
    setDraft(prompt);
    setOpen(true);
  }, []);

  // Raccourci Ctrl/Cmd + J
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const value = useMemo<AssistantContextValue>(
    () => ({ open, setOpen, openWithPrompt }),
    [open, openWithPrompt],
  );

  const enabled = Boolean(user) && roles.length > 0;
  const hasJourneyActions =
    location.pathname.endsWith("/parcours") || location.pathname.endsWith("/dossier/wizard");

  return (
    <AssistantContext.Provider value={value}>
      {children}
      {enabled ? (
        <>
          {/* Bouton flottant */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={cn(
              "focus-ring fixed right-5 z-40 grid place-items-center rounded-full",
              hasJourneyActions ? "bottom-28" : "bottom-5",
              "bg-brand text-brand-foreground shadow-brand transition-all hover:scale-105 hover:bg-brand-hover active:scale-95",
              open && "pointer-events-none scale-90 opacity-0",
            )}
            style={{ height: "3.25rem", width: "3.25rem" }}
            aria-label={t("ui.assistant.open")}
            title={`${t("ui.assistant.open")} (Ctrl+J)`}
          >
            <NoaIcon className="size-6" />
          </button>

          <AssistantPanel
            open={open}
            onClose={() => setOpen(false)}
            route={location.pathname}
            initialDraft={draft}
            onDraftConsumed={() => setDraft("")}
          />
        </>
      ) : null}
    </AssistantContext.Provider>
  );
}
